import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useBusinesses } from "@/hooks/use-businesses";
import { useLocation } from "wouter";

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  price: number;
  monthlyPrice?: string | number | null;
  features: string[];
  stripePriceId?: string;
  isActive: boolean;
}

export interface BusinessSubscription {
  id: number;
  businessId: number;
  planId: number;
  plan?: SubscriptionPlan;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  status: string;
  startDate: string;
  endDate?: string;
}

export const useSubscriptionPlans = () => {
  const { toast } = useToast();

  const result = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/subscription-plans");
      return response.json();
    }
  });

  // Handle errors in useEffect to avoid infinite re-render loop
  useEffect(() => {
    if (result.error) {
      toast({
        title: "Failed to load subscription plans",
        description: result.error.message || "An error occurred while loading subscription plans",
        variant: "destructive",
      });
    }
  }, [result.error]);

  return result;
};

export const useSubscription = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeBusiness, businesses } = useBusinesses();

  const businessId = activeBusiness?.id;

  // Fetch subscription plans
  const {
    data: plans = [],
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    error
  } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/subscription-plans");
      return response.json();
    }
  });
  
  // Handle errors in useEffect to avoid infinite re-render loop
  useEffect(() => {
    if (error) {
      toast({
        title: "Failed to load subscription plans",
        description: error.message || "An error occurred while loading subscription plans",
        variant: "destructive",
      });
    }
  }, [error]);

  // Tenant-level subscription from billing API (single source of truth)
  const { data: billingSubscription } = useQuery<any>({
    queryKey: ["/api/billing/subscription"],
    queryFn: async () => {
      const res = await fetch("/api/billing/subscription", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000,
    enabled: !!businessId,
  });

  const activeSubscription = billingSubscription || null;

  // Find current plan
  const currentPlan = activeSubscription ? plans.find(p => p.id === activeSubscription.planId) : null;

  // Subscribe to a plan mutation
  const subscribeToNewPlanMutation = useMutation({
    mutationFn: async (planId: number) => {
      if (!businessId) throw new Error("No active business selected");
      const response = await apiRequest(
        "POST",
        `/api/businesses/${businessId}/subscriptions`,
        { planId }
      );
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
      toast({
        title: "Subscription created",
        description: "Your subscription has been created successfully",
      });
      
      // If we have a client secret for payment, we could handle Stripe checkout here
      if (data.clientSecret) {
        // Handle payment flow if needed
      }
    },
    onError: (err: any) => {
      toast({
        title: "Failed to create subscription",
        description: err.message || "An error occurred while creating your subscription",
        variant: "destructive",
      });
    },
  });
  
  // Create checkout session mutation
  const createCheckoutSessionMutation = useMutation({
    mutationFn: async (planId: number) => {
      if (!businessId) throw new Error("No active business selected");
      
      // Get current origin for success and cancel URLs
      const origin = window.location.origin;
      const successUrl = `${origin}/dashboard?subscription=success`;
      const cancelUrl = `${origin}/subscription/plans?subscription=cancelled`;
      
      const response = await apiRequest(
        "POST",
        `/api/businesses/${businessId}/subscription/checkout`,
        { 
          planId,
          successUrl,
          cancelUrl
        }
      );
      
      return response.json();
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      if (data.sessionUrl) {
        // For external URLs like Stripe, we still need to use window.location.href
        // This is not causing the double slash issue since it's an external URL
        window.location.href = data.sessionUrl;
      } else {
        toast({
          title: "Error",
          description: "No checkout URL was returned from the server",
          variant: "destructive",
        });
      }
    },
    onError: (err: any) => {
      toast({
        title: "Failed to create checkout session",
        description: err.message || "An error occurred while creating your checkout session",
        variant: "destructive",
      });
    },
  });

  // Update subscription plan mutation
  const updateSubscriptionMutation = useMutation({
    mutationFn: async ({ subscriptionId, planId }: { subscriptionId: number; planId: number }) => {
      if (!businessId) throw new Error("No active business selected");
      const response = await apiRequest(
        "PUT",
        `/api/businesses/${businessId}/subscriptions/${subscriptionId}`,
        { planId }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
      toast({
        title: "Subscription updated",
        description: "Your subscription has been updated successfully",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to update subscription",
        description: err.message || "An error occurred while updating your subscription",
        variant: "destructive",
      });
    },
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (subscriptionId: number) => {
      if (!businessId) throw new Error("No active business selected");
      const response = await apiRequest(
        "DELETE",
        `/api/businesses/${businessId}/subscriptions/${subscriptionId}`,
        null
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
      toast({
        title: "Subscription cancelled",
        description: "Your subscription has been cancelled successfully",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to cancel subscription",
        description: err.message || "An error occurred while cancelling your subscription",
        variant: "destructive",
      });
    },
  });

  return {
    plans,
    currentPlan,
    activeSubscription,
    isLoadingPlans,
    isErrorPlans,
    subscribeToNewPlan: (planId: number) => subscribeToNewPlanMutation.mutate(planId),
    updateSubscription: (subscriptionId: number, planId: number) =>
      updateSubscriptionMutation.mutate({ subscriptionId, planId }),
    cancelSubscription: (subscriptionId: number) => cancelSubscriptionMutation.mutate(subscriptionId),
    createCheckoutSession: (planId: number) => createCheckoutSessionMutation.mutate(planId),
    isPendingSubscribe: subscribeToNewPlanMutation.isPending,
    isPendingUpdate: updateSubscriptionMutation.isPending,
    isPendingCancel: cancelSubscriptionMutation.isPending,
    isPendingCheckout: createCheckoutSessionMutation.isPending,
  };
};
